/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponentProps, ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { ChannelDeliveryOptionConfiguration, ProductDeliveryOptions, SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import React from 'react';

import { IOrderDetailsTableHeaderResources, OrderDetailsTableHeaderComponent } from './components/order-details-table-header-component';
import { IOrderDetailsTableRowResources, OrderDetailsTableRowComponent } from './components/order-details-table-row-component';

/**
 * Interface with the data required for order details.
 */
export interface IOrderDetailsTableData {
    order: SalesOrder | undefined;
    products: SimpleProduct[];
    deliveryOptions: ProductDeliveryOptions[];
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
}

/**
 * Resources needed to render order details table.
 */
export interface IOrderDetailsTableResources extends IOrderDetailsTableHeaderResources, IOrderDetailsTableRowResources {}

/**
 * Configuration of order details table.
 */
export interface IOrderDetailsTableProps extends IComponentProps<IOrderDetailsTableData> {
    context: ICoreContext;
    className: string;

    imageSettings?: IImageSettings;
    isRetailMultiplePickUpOptionEnabled?: boolean;

    resources: IOrderDetailsTableResources;
}

/**
 * Renders order details table.
 * @param props - Configuration for order details table.
 * @returns React node with the table.
 */
export const OrderDetailsTableComponent: React.FC<IOrderDetailsTableProps> = (props: IOrderDetailsTableProps) => {
    const rowClassName = `${props.className}__row`;
    const headerClassName = `${props.className}__header`;

    return (
        <table className={props.className}>
            <OrderDetailsTableHeaderComponent
                context={props.context}
                typeName={props.typeName}
                id={headerClassName}
                className={headerClassName}
                resources={props.resources}
                data={{}}
            />
            {
                props.data.order?.SalesLines?.map((salesLine, index) => {
                    const salesOrderProduct = props.data.products.find(product => product.RecordId === salesLine.ProductId);
                    return salesOrderProduct && (
                        <OrderDetailsTableRowComponent
                            context={props.context}
                            typeName={props.typeName}
                            id={`${rowClassName}-${index}`}
                            key={salesLine.ItemId}
                            className={rowClassName}
                            resources={props.resources}
                            imageSettings={props.imageSettings}
                            isRetailMultiplePickUpOptionEnabled={props.isRetailMultiplePickUpOptionEnabled}
                            data={{
                                salesLine,
                                salesOrder: props.data.order!,
                                product: salesOrderProduct,
                                deliveryOptions: props.data.deliveryOptions,
                                channelDeliveryOptionConfig: props.data.channelDeliveryOptionConfig
                            }}
                        />
                    );
                })
            }
        </table>
    );
};
